import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { FeedbackService } from '../../services/feedback.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { RequiredTrimmedValidator } from '../../validators/required-trimmed.validator';
import { SnackBarService, SnackBarTypes } from '@my7n/ui';
import { FEEDBACK_MODAL_CATEGORIES, IFeedbackModalAdditionalData } from '../../interfaces/feedback-modal';
import cloneDeep from 'lodash-es/cloneDeep';
import { GlobalCommonFacadeService } from '../../services/facades/global-common-facade.service';
import { take } from 'rxjs';

@Component({
    selector: 'feedback-modal',
    templateUrl: './feedback-modal.component.html',
    styleUrls: ['./feedback-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FeedbackModalComponent implements OnInit {
  /**
   * Feedback form reference (based on reactive form)
   */
  feedbackForm: FormGroup<{ feedbackType: FormControl<string | null>; feedbackContent: FormControl<string | null>; }>;
  categories = cloneDeep(FEEDBACK_MODAL_CATEGORIES);
  appVersion: string;
  sending = false;

  readonly VALIDATORS = {
    ContentMaxLength: 20000
  }

  constructor(public dialogRef: MatDialogRef<FeedbackModalComponent>,
              private feedbackService: FeedbackService,
              private snackBarService: SnackBarService,
              private globalCommonFacadeService: GlobalCommonFacadeService,
              private cdr: ChangeDetectorRef) { }

  ngOnInit() {
    this.createForm();

    this.globalCommonFacadeService.appVersion$.pipe(take(1)).subscribe(version => {
      this.appVersion = version;
    });
  }

  /**
   * Registers reactive form for editing event.
   */
  private createForm() {
    this.feedbackForm = new FormGroup({
      feedbackType: new FormControl(null, Validators.required),
      feedbackContent: new FormControl('', [RequiredTrimmedValidator, Validators.maxLength(this.VALIDATORS.ContentMaxLength)]),
    });
  }

  onSubmit(): boolean {
    this.feedbackForm.markAllAsTouched();
    this.sending = true;

    if (!this.feedbackForm.valid) {
      console.debug('[FeedbackModalComponent][onSubmit] Form is invalid', this.feedbackForm.value);
      this.sending = false;
      this.cdr.markForCheck();
      return false;
    }

    this.snackBarService.openPending({
      message: 'Sending feedback',
      type: SnackBarTypes.Notification
    });

    this.feedbackService.sendEmail(
      this.feedbackForm.value.feedbackType,
      this.feedbackForm.value.feedbackContent.trim(),
      this.prepareDetails()
    ).then(() => {
      this.dialogRef.close();
      this.snackBarService.open({
        message: 'Your feedback has been sent, thank you!',
        type: SnackBarTypes.SuccessAlt
      });
    }, () => {
      this.snackBarService.open({
        message: 'There was a problem while sending your feedback',
        type: SnackBarTypes.ErrorAlt
      });
    })
    .finally(() => {
      this.sending = false;
      this.cdr.markForCheck();
    });
  }

  private prepareDetails(): IFeedbackModalAdditionalData {
    const userInfo: IFeedbackModalAdditionalData = {
      CurrentUrl: window.location.href,
      AppVersion: this.appVersion,
      UserOS: window.navigator.platform,
      UserAgent: window.navigator.userAgent,
      UserLanguage: window.navigator.language,
    };

    return userInfo;
  }

  closeDialog(): void {
    this.dialogRef.close();
  }
}

